import {
  AbsoluteCenter,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  Heading,
  IconButton,
  Input,
  Link,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {
  calcInstallmentAmortizationApi,
  createInstallmentApi, getAmortizationApi,
  getDealApi,
  getDealsApi,
  getInstallmentApi,
  getInstallmentRepaymentsApi,
  updateInstallmentApi,
} from 'api/dealApi';
import { getUnderwritingListApi } from 'api/underwritingApi';
import { TwDatePicker } from 'components/DatePicker';
import {
  CurrencyNumberInput,
  PercentageNumberInput,
  TwNumberInput,
} from 'components/NumberInput';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import Card from 'components/card/Card';
import IrrCapPeriodInput from 'components/inputs/IrrCapPeriodInput';
import SelectInput from 'components/inputs/SelectInput';
import BackLink from 'components/links/BackLink';
import {
  INSTALLMENT_OWNER_TYPE,
  INSTALLMENT_OWNER_TYPE_META,
  INSTALLMENT_STATUS,
} from 'constants/installmentConstants';

import {
  DEAL_TYPE,
  FEE_TYPE,
  INSTALLMENT_TYPE,
  INSTALLMENT_TYPE_META,
  REPAYMENT_FREQUENCY_TYPE_META,
  REVENUE_RECOGNITION_TYPE,
  TOTAL_TYPE_META,
  isIrrCapFeeType,
} from 'constants/dealConstants';

import { AddIcon } from '@chakra-ui/icons';
import Decimal from 'decimal.js';
import { generateSelectOptions } from 'helpers/inputHelper';
import { hasObjectAllValues } from 'helpers/objectHelper';
import { formatRequiredFieldLabel } from 'helpers/requiredFieldLabel';
import { useFeeTypeOptions } from 'hooks/useFeeTypeOptions';
import { useTwelveBankAccounts } from 'hooks/useTwelveBankAccounts';
import cloneDeep from 'lodash.clonedeep';
import moment from 'moment';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { cohortMonthEnd, cohortMonthStart } from 'utils/cohortMonth';
import { IRR_CAP_PERIOD } from 'utils/irrCapPeriod';
import { resolveAccelerationOnLoad, validateAcceleration } from '../accelerationHelper';
import { mapRoasThresholdsFromApi, mapRoasThresholdsToApi } from '../roasCovenantMonth';
import { emptyRoasVectorRow, hasDuplicateRoasVectorMonths, mapRoasVectorFromApi, mapRoasVectorToApi } from '../roasVector';
import { useMarketingViews } from 'hooks/useMarketingViews';
import InstallmentRepaymentsTable from '../InstallmentRepaymentsTable';
import InstallmentStatusTag from '../InstallmentStatusTag';
import RevenueRecognitionSelect from '../RevenueRecognitionSelect';
import { useRepaymentSchedule } from '../hooks/useRepaymentSchedule';
import InstallmentSettings from './InstallmentSettings';
import { getInstallmentApplyMissingReasons } from './installmentApplyValidation';
import {
  resolvePersistedFileName,
  shouldApplyFetchedContractFile,
} from './installmentContractDocuments';
import RepaymentsScheduleSettings from './RepaymentsScheduleSettings';
import TotalFeeSummary from './TotalFeeSummary';
import {createContractDocumentApi, getContractDocumentApi} from "../../../../api/contractDocumentApi";
import FileInput from "../../../../components/inputs/FileInput";
import DownloadLink from "../../../../components/links/DownloadLink";
import {getFileContent} from "../../../../helpers/formHelper";

const getTotalPurchasePrice = (purchasePrices) => {
  return Decimal.sum(...purchasePrices.map((a) => a.amount || 0)).toNumber();
};

const createFields = ({
  underwritingOptions,
  repaymentDaysOptions,
  feeTypeOptions,
  daysBeforeEndOfMonthOptions,
  dealOptions,
  formValues,
  isNewInstallment,
  bankAccountOptions,
  onFieldChange,
  dealType,
  isPageDisabled,
  purchasePriceFile,
  purchasePriceFileContent,
  purchasePriceFileLoading,
  onSelectFiles,
  marketingViewOptions,
}) => {
  const fields = [
    {
      label: 'Installment Type',
      name: 'installmentType',
      element: SelectInput,
      required: true,
      isBoolean: true,
      placeholder: 'Select Installment Type',
      options: generateSelectOptions({ metaObj: INSTALLMENT_TYPE_META }),
      isDisabled: formValues.installmentType === INSTALLMENT_TYPE.FUNDED, //!isNewInstallment,
    },
    {
      label: 'Owner',
      name: 'owner',
      element: SelectInput,
      required: true,
      isDisabled: true,
      placeholder: 'Select Owner',
      options: generateSelectOptions({ metaObj: INSTALLMENT_OWNER_TYPE_META }),
      formatValue: (value) => {
        return INSTALLMENT_OWNER_TYPE_META[value]?.label;
      },
    },
    {
      label: 'Underwriting Report',
      name: 'underwritingId',
      element: SelectInput,
      placeholder: 'Select Underwriting',
      options: underwritingOptions,
      required: false,
      isHidden: formValues.installmentType !== INSTALLMENT_TYPE.FUNDED,
    },
    {
      label: 'Transfer Date',
      name: 'transferDate',
      element: TwDatePicker,
      required: true,
        isDisabled: formValues.amortization?.length > 0,
    },
    {
      label: 'Contribution Date',
      name: 'contributionDate',
      element: TwDatePicker,
      required: false,
      isHidden: true,
    },

    {
      label: 'Purchase Price Document (PDF)',
      name: 'purchasePriceDocument',
      required: true,
      isCustomField: true,
      element: (
          <>
          <Text>{formatRequiredFieldLabel('Purchase Price Document (PDF)', true)}</Text>
          <Box w={200}>
            <Flex gap={1} direction={'column'}>
              <FileInput
                  initialFile={purchasePriceFile}
                  allowedFileTypes={['application/pdf']}
                  onSelectFiles={onSelectFiles}
                  disabled={isPageDisabled || formValues.amortization?.length > 0}
                  placeholder={'Select Purchase Price file'}
              />
              {purchasePriceFileLoading ? (
                <Flex align="center" gap={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm">Loading PDF…</Text>
                </Flex>
              ) : purchasePriceFile ? (
                <DownloadLink
                  filename={purchasePriceFile.name}
                  content={purchasePriceFileContent}
                  mimeType="application/pdf"
                />
              ) : null}
            </Flex>
          </Box>
            <Box></Box>
            <Box></Box>
          </>
      ),
    },

    {
      name: 'purchasePrices',
      isCustomField: true,
      element: (
          <PurchasePriceToBankAccountsSection
              formValues={formValues}
              onFieldChange={onFieldChange}
              bankAccountOptions={bankAccountOptions}
              isDisabled={formValues.amortization?.length > 0}
              // isDisabled={!isNewInstallment || formValues.amortization?.length > 0}
              installmentType={formValues.installmentType}
          />
      ),
      required: true,
    },
    {
      label: 'Deal',
      name: 'dealId',
      element: SelectInput,
      required: true,
      placeholder: 'Select Deal',
      options: dealOptions,
      isDisabled: !isNewInstallment,
      showValueTooltip: true,
    },
    {
      label: 'Funding Percentage',
      name: 'fundingPercentage',
      element: PercentageNumberInput,
      placeholder: '0',
      required: true,
      tooltip: 'The percentage of UA for each funding draw',
    },
    {
      label: 'Marketing view',
      name: 'marketingViewId',
      element: SelectInput,
      placeholder: 'Default',
      options: marketingViewOptions,
      isHidden: !marketingViewOptions?.length,
      tooltip: 'Custom marketing view used to size funding: funding amount = view spend × funding %. Covenants always use all marketing. Repayment % uses all marketing unless the box below is checked.',
      helpLink: 'marketing-view-funding',
    },
    {
      label: 'Also use this view for repayment %',
      name: 'marketingViewForRepayment',
      element: Checkbox,
      isHidden: !marketingViewOptions?.length || !formValues.marketingViewId,
      tooltip: 'Checked: repayment % = purchase price ÷ view spend, so you collect a higher share of revenue. Unchecked: repayment % = purchase price ÷ all marketing spend.',
      helpLink: 'marketing-view-funding',
    },
    {
      label: 'Max Funding Amount for Cohort',
      name: 'maxFundingAmount',
      element: CurrencyNumberInput,
      placeholder: 'e.g., 20000000',
      tooltip: 'The maximum amount that can be funded per cohort, e.g., $2 million',
    },
    {
      label: 'Max Exposure',
      name: 'maxExposure',
      element: CurrencyNumberInput,
      placeholder: 'e.g., 5000000',
      tooltip: 'The maximum total exposure including the current funding at any given time',
    },
    {
      label: 'Max MOM Growth',
      name: 'maxMomGrowth',
      element: PercentageNumberInput,
      tooltip: 'The maximum % by which funding can grow from one month to the next (vs the previous month\'s funding amount)',
    },
    {
      label: 'Max MOM Funding Amount',
      name: 'maxMomFundingAmount',
      element: CurrencyNumberInput,
      placeholder: 'e.g., 5000000',
      tooltip: 'The maximum funding amount allowed given the previous funding\'s MOM growth cap',
    },
    {
      label: 'Max CAC',
      name: 'maxCac',
      element: CurrencyNumberInput,
      placeholder: 'e.g., 250',
      tooltip: 'The maximum customer acquisition cost (CAC) allowed',
    },
    {
      label: 'MOM Delta (%)',
      name: 'momDeltaPct',
      element: PercentageNumberInput,
      placeholder: '0',
      tooltip: 'Minimum required month-over-month ROAS growth for the funded cohort. Each month, the cohort\'s cumulative ROAS must increase by at least this many percentage points versus the previous month (the first month is measured against 0). If a month\'s growth falls short, a "Min ROAS Growth" alert is raised.',
    },
    {
      label: 'MOM Delta Until ROAS (%)',
      name: 'momDeltaUntilRoasPct',
      element: PercentageNumberInput,
      placeholder: 'e.g., 100',
      tooltip: 'Stop checking the MOM Delta growth constraint once cumulative ROAS reaches this level (e.g. 100%). Leave empty to keep checking indefinitely.',
    },
    {
      label: 'ROAS Covenant Revenue Type',
      name: 'roasCovenantRevenueTotalType',
      element: SelectInput,
      placeholder: 'Gross (default)',
      options: generateSelectOptions({ metaObj: TOTAL_TYPE_META }),
      tooltip: 'Gross or net revenue used for ROAS threshold and MOM delta covenants. Empty uses gross (deal default).',
    },
    {
      label: 'ROAS Covenant Multiplier (%)',
      name: 'roasCovenantMultiplier',
      element: PercentageNumberInput,
      placeholder: '100',
      tooltip:
        'Percent of covenant revenue counted toward ROAS (e.g. 60% with $200k gross counts as $120k). Leave empty for 100%.',
    },
    {
      label: '# of past cohorts to check',
      name: 'pastCohortsToCheck',
      element: TwNumberInput,
      placeholder: '0',
      tooltip: 'The minimum number of past cohorts we have to check that they do not breach the ROAS covenant',
    },
    {
      label: (
        <>
          Min.{' '}
          <Link href="/help#run-rate-cashflow-model" isExternal color="blue.500" textDecoration="underline">
            Runrate
          </Link>
        </>
      ),
      name: 'minRunrate',
      element: TwNumberInput,
      placeholder: '0',
      tooltip: 'The minimal runrate the customer needs to get a loan',
    },
    {
      label: 'Min. Cash Balance',
      name: 'minCashBalance',
      element: CurrencyNumberInput,
      placeholder: '0',
      tooltip: 'The minimum cash available in the bank accounts the customer needs to get a loan',
    },
    {
      label: 'Repayment Terms',
      name: 'repaymentTerms',
      element: Input,
      tooltip: 'The specific terms of the commitment, e.g., discretionary draws or automatic funding',
      required: false,
    },
    {
      label: 'Payment Frequency',
      name: 'repaymentFrequency',
      element: SelectInput,
      required: true,
      placeholder: 'Select Payment Frequency',
      options: generateSelectOptions({
        metaObj: REPAYMENT_FREQUENCY_TYPE_META,
      }),
      formatValue: (value) => {
        return REPAYMENT_FREQUENCY_TYPE_META[value]?.label;
      },
      tooltip: 'How often the customer has to make a repayments',
    },
    {
      label: 'Repayment Days',
      name: 'repaymentDays',
      element: SelectInput,
      required: true,
      placeholder: 'Select Repayment Days',
      options: repaymentDaysOptions,
      isMulti: true,
      isDisabled: !formValues.repaymentFrequency,
      tooltip: 'When repayment is due - select specific day of month or end of month or on what weekday',
    },
    {
      label: 'Days Before End of Month',
      name: 'daysBeforeEndOfMonth',
      element: SelectInput,
      required: true,
      isNumber: true,
      options: daysBeforeEndOfMonthOptions,
      isHidden: !formValues?.repaymentDays?.includes(-1),
      tooltip: 'Number of days before month end to process the repayment',
    },
    {
      label: 'Fee Type',
      name: 'feeSettings.feeType',
      element: SelectInput,
      options: feeTypeOptions,
      placeholder: '-- Select Fee Type --',
      tooltip:
        'The method by which fees are calculated. For IRR Cap Daily, see Help → IRR Cap Daily.',
      helpLink: 'irr-cap-daily',
      required: true,
    },
    {
      label: 'Fee',
      name: 'feeSettings.feePct',
      element: PercentageNumberInput,
      tooltip: 'The fixed percentage fee charged on each funding draw',
      required: formValues.feeSettings?.feeType === FEE_TYPE.FIXED,
      isHidden: formValues.feeSettings?.feeType !== FEE_TYPE.FIXED,
    },
    {
      label: 'IRR Cap',
      name: 'feeSettings.irrCapPct',
      element: IrrCapPeriodInput,
      tooltip: 'Enter as monthly or annual. The typed rate and period are saved; monthly is converted to annual only when calculating fees and exposure.',
      required: isIrrCapFeeType(formValues.feeSettings?.feeType),
      isHidden: !isIrrCapFeeType(formValues.feeSettings?.feeType),
      props: {
        period: formValues.feeSettings?.irrCapPeriod || IRR_CAP_PERIOD.ANNUAL,
      },
    },
    {
      label: 'First Repayment Date',
      name: 'firstRepaymentDate',
      element: TwDatePicker,
      required: true,
    },
    {
      label: 'Max Number of Repayments',
      name: 'numberOfRepayments',
      element: TwNumberInput,
      tooltip: 'The maximum number of repayments the customer has per funding',
      required: true,
    },
    {
      label: 'Is True Up',
      name: 'isTrueUp',
      element: Checkbox,
      isDisabled: formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP || formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP_DAILY,
      tooltip:
        formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP ||
        formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP_DAILY
          ? 'Required when Fee Type is IRR Cap'
          : undefined,
    },
    {
      label: 'Enable Acceleration',
      name: 'accelerationEnabled',
      element: Checkbox,
      tooltip:
        'When enabled, you can set ROAS thresholds. When the cohort meets those thresholds, acceleration kicks in and repayments use a percentage of the selected Gross/Net revenue instead of the standard revenue margin.',
    },
    {
      label: 'Acceleration %',
      name: 'accelerationPct',
      element: TwNumberInput,
      placeholder: 'e.g. 100',
      required: formValues.accelerationEnabled === true,
      isHidden: formValues.accelerationEnabled !== true,
      tooltip: 'The percentage of the selected revenue type applied when acceleration is activated (e.g. 100 = 100%)',
    },
    {
      label: 'Acceleration Revenue Type',
      name: 'accelerationRevenueTotalType',
      element: SelectInput,
      required: formValues.accelerationEnabled === true,
      isHidden: formValues.accelerationEnabled !== true,
      placeholder: 'Select revenue type',
      options: generateSelectOptions({ metaObj: TOTAL_TYPE_META }),
      tooltip: 'The Gross/Net revenue used when acceleration is activated',
    },
    {
      label: 'Fee Collection at Last Payment',
      name: 'feeCollectionAtLastPayment',
      element: Checkbox,
      isHidden:
        formValues.feeSettings?.feeType !== FEE_TYPE.IRR_CAP &&
        formValues.feeSettings?.feeType !== FEE_TYPE.IRR_CAP_DAILY,
      tooltip:
        'When checked, fee is collected at the final repayment. When unchecked (default), fee is collected in regular payments over the schedule.',
    },
  ];

  const roasFields = [
    {
      label: 'Cohort Month',
      name: 'cohortStartDate',
      element: TwDatePicker,
      required: dealType === DEAL_TYPE.ROAS,
      tooltip:
        'Revenue cohort month. The first and last day of that month are saved as the cohort start and end dates.',
      props: {
        showMonthYearPicker: true,
        dateFormat: 'MMMM yyyy',
      },
    },
    {
      label: 'Cohort End Date',
      name: 'cohortEndDate',
      element: TwDatePicker,
      required: dealType === DEAL_TYPE.ROAS,
      isHidden: true,
    },
    {
      label: 'Revenue Margin',
      name: 'pctFromRevenueShare',
      element: PercentageNumberInput,
      required: dealType === DEAL_TYPE.ROAS,
    },
    {
      label: 'Revenue Type',
      name: 'revenueTotalType',
      element: SelectInput,
      required: dealType === DEAL_TYPE.ROAS,
      placeholder: 'Select Revenue Type',
      options: generateSelectOptions({ metaObj: TOTAL_TYPE_META }),
    },
  ];

  return [...fields, ...roasFields];
};

const installmentSettingsFields = [
  'installmentType',
  'dealId',
  'underwritingId',
  'owner',
  'transferDate',
  'purchasePrices',
  'repaymentTerms',
  'fundingPercentage',
  'marketingViewId',
  'marketingViewForRepayment',
  'maxFundingAmount',
  'maxExposure',
  'maxMomGrowth',
  'maxMomFundingAmount',
  'maxCac',
  'momDeltaPct',
  'momDeltaUntilRoasPct',
  'roasCovenantRevenueTotalType',
  'roasCovenantMultiplier',
  'pastCohortsToCheck',
  'minRunrate',
  'minCashBalance',
  'purchasePriceDocument',
];

const repaymentScheduleFields = [
  'repaymentFrequency',
  'repaymentDays',
  'daysBeforeEndOfMonth',
  'feeSettings.feeType',
  'feeSettings.feePct',
  'feeSettings.irrCapPct',
  'firstRepaymentDate',
  'numberOfRepayments',
];

const repaymentScheduleRoasFields = [
  'cohortStartDate',
  'cohortEndDate',
  'firstRepaymentDate',
  'repaymentFrequency',
  'repaymentDays',
  'daysBeforeEndOfMonth',
  'numberOfRepayments',
  'feeSettings.feeType',
  'feeSettings.feePct',
  'feeSettings.irrCapPct',
  'revenueTotalType',
  'pctFromRevenueShare',
  'isTrueUp',
  'feeCollectionAtLastPayment',
  'accelerationEnabled',
  'accelerationPct',
  'accelerationRevenueTotalType',
];

const PurchasePriceToBankAccountsSection = ({
  formValues,
  onFieldChange,
  bankAccountOptions,
  isDisabled,
  installmentType,
}) => {
  let { purchasePrices } = formValues;

  if (!purchasePrices) {
    purchasePrices = [{ amount: null, bankAccountId: formValues.installmentType === INSTALLMENT_TYPE.FUNDED ? null : bankAccountOptions[0].value }];
  }

  return (
    <>
      <Text>{formatRequiredFieldLabel('Purchase Price', true)}</Text>
      {purchasePrices.map((item, index) => {
        return (
          <Fragment key={index}>
            {index !== 0 && (<Box></Box>)}
            <Box w={200}>
              <CurrencyNumberInput
                value={item.amount}
                onChange={(value) => {
                  purchasePrices[index].amount = value;
                  onFieldChange('purchasePrices', purchasePrices);
                }}
                inputProps={{ fontWeight: 800, w: 200 }}
                showPrefix={true}
                isDisabled={isDisabled}
              />
            </Box>
            <Box>
              {installmentType === INSTALLMENT_TYPE.FUNDED && (
                <SelectInput
                  label={'Bank Account'}
                  required={true}
                  placeholder={'Select Bank Account'}
                  options={bankAccountOptions}
                  value={item.bankAccountId}
                  onChange={(value) => {
                    purchasePrices[index].bankAccountId = value;
                    onFieldChange('purchasePrices', purchasePrices);
                  }}
                  fontWeight={800}
                  w={200}
                  // ???
                  // isDisabled={item.bankAccountId && isDisabled}
                />
              )}
            </Box>
            <Box>
            {!isDisabled && index === 0 && formValues.installmentType === INSTALLMENT_TYPE.FUNDED  && (
              <IconButton
                icon={<AddIcon />}
                variant={'outline'}
                borderRadius={8}
                boxSize={8}
                onClick={() => {
                  purchasePrices.push({
                    amount: null,
                    bankAccountId: formValues.installmentType === INSTALLMENT_TYPE.FUNDED ? null : bankAccountOptions[0].value,
                  });
                  onFieldChange('purchasePrices', purchasePrices);
                }}
              />
            )}
            {!isDisabled && index > 0 && (
              <Button
                variant={'link'}
                color={'red.500'}
                onClick={() => {
                  purchasePrices.splice(index, 1);
                  onFieldChange('purchasePrices', purchasePrices);
                }}
              >
                Remove
              </Button>
            )}
            </Box>
          </Fragment>
        );
      })}
      <Box></Box>
      <Box w={200}>
        <CurrencyNumberInput
            value={getTotalPurchasePrice(purchasePrices)}
            isDisabled={true}
            showPrefix={true}
            inputProps={{ fontWeight: 800, w: 200 }}
        />
      </Box>
      <Box></Box>
      <Box></Box>
    </>
  );
};

const Installment = () => {
  const [loading, setLoading] = useState(true);
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [saveStepMessage, setSaveStepMessage] = useState(null);
  const [deals, setDeals] = useState([]);
  const [dealType, setDealType] = useState(null);
  const [underwritingOptions, setUnderwritingOptions] = useState(null);
  const [revenueRecognition, setRevenueRecognition] = useState(
      REVENUE_RECOGNITION_TYPE.INTEREST_FIRST
  );
  const [amortizationData, setAmortizationData] = useState({
    amortization: [],
    amountSold: null,
  });
  const [status, setStatus] = useState(null);
  const [installmentRepayments, setInstallmentRepayments] = useState(null);
  const [totalFeeMetrics, setTotalFeeMetrics] = useState({
    totalFeeAmount: null,
    totalFeePct: null,
  });
  const [installmentRepaymentsLoading, setInstallmentRepaymentsLoading] =
    useState(false);

  const { handleSubmit, reset, control, watch, setValue } = useForm({
    defaultValues: {
      owner: INSTALLMENT_OWNER_TYPE.TWELVE,
    },
  });

  const { feeTypeOptions } = useFeeTypeOptions({ dealType });

  const toast = useToast();
  const navigate = useNavigate();
  const formValues = watch();
  const { repaymentFrequency, installmentType } = formValues;

  const includeBankAccountIds = useMemo(() => {
    const accountIds = new Set();
    (formValues.purchasePrices || []).forEach((purchasePriceRow) => {
      if (purchasePriceRow?.bankAccountId) {
        accountIds.add(purchasePriceRow.bankAccountId);
      }
    });
    (installmentRepayments || []).forEach((repayment) => {
      if (repayment?.bankAccountId) {
        accountIds.add(repayment.bankAccountId);
      }
    });
    return [...accountIds];
  }, [formValues.purchasePrices, installmentRepayments]);

  const { bankAccountOptions, isTwelveBankAccountsLoading } =
    useTwelveBankAccounts({ includeAccountIds: includeBankAccountIds });
  const [dealContractFile, setDealContractFile] = useState(null);
  const [dealContractFileContent, setDealContractFileContent] = useState(null);
  const [dealContractFileLoading, setDealContractFileLoading] = useState(false);
  const [purchasePriceFile, setPurchasePriceFile] = useState(null);
  const [purchasePriceFileContent, setPurchasePriceFileContent] = useState(null);
  const [purchasePriceFileLoading, setPurchasePriceFileLoading] = useState(false);
  const purchasePriceUserSelectedRef = useRef(false);
  const dealContractUserSelectedRef = useRef(false);
  const [roasThresholds, setRoasThresholds] = useState([{ id: 1, month: '', minRoasPct: '' }]);
  const [roasVector, setRoasVector] = useState([emptyRoasVectorRow()]);

  const {
    repaymentDaysOptions,
    onRepaymentFrequencyFieldChange,
    daysBeforeEndOfMonthOptions,
  } = useRepaymentSchedule({ repaymentFrequency });
  const { customerId, installmentId } = useParams();
  const { options: marketingViewOptions } = useMarketingViews(customerId);

  useEffect(() => {
    const abortCtrl = new AbortController();
    const loadContractDocuments = async ({
      abortCtrl: pdfAbortCtrl,
      purchasePriceFileName,
      dealContractFileName,
      dealId,
    }) => {
      const loadPurchasePrice = async () => {
        if (!purchasePriceFileName) {
          return;
        }
        try {
          const installmentFile = await getContractDocumentApi({
            customerId,
            dealOrInstallmentId: installmentId,
            fileName: purchasePriceFileName,
          });
          if (
            !shouldApplyFetchedContractFile({
              aborted: pdfAbortCtrl.signal.aborted,
              userAlreadySelected: purchasePriceUserSelectedRef.current,
            })
          ) {
            return;
          }
          if (installmentFile) {
            setPurchasePriceFile(installmentFile);
            setPurchasePriceFileContent(installmentFile);
          }
        } finally {
          if (!pdfAbortCtrl.signal.aborted) {
            setPurchasePriceFileLoading(false);
          }
        }
      };

      const loadDealContract = async () => {
        if (!dealContractFileName) {
          return;
        }
        try {
          const dealFile = await getContractDocumentApi({
            customerId,
            dealOrInstallmentId: dealId,
            fileName: dealContractFileName,
          });
          if (
            !shouldApplyFetchedContractFile({
              aborted: pdfAbortCtrl.signal.aborted,
              userAlreadySelected: dealContractUserSelectedRef.current,
            })
          ) {
            return;
          }
          if (dealFile) {
            setDealContractFile(dealFile);
            setDealContractFileContent(dealFile);
          }
        } finally {
          if (!pdfAbortCtrl.signal.aborted) {
            setDealContractFileLoading(false);
          }
        }
      };

      await Promise.all([loadPurchasePrice(), loadDealContract()]);
    };

    const init = async () => {
      try {
        const p1 = getUnderwritingOptions();
        if (installmentId) {
          const [options, installment, _deals] = await Promise.all([
            p1,
            getInstallment(),
            getDeals(),
          ]);
          if (abortCtrl.signal.aborted) return;

          setUnderwritingOptions(options);
          setDeals(_deals);

          const info = { ...installment.info };
          const feeAtLast = info.repaymentSettings?.feeCollectionAtLastPayment ?? info.feeCollectionAtLastPayment;
          info.feeCollectionAtLastPayment = feeAtLast === undefined ? false : feeAtLast;
          Object.assign(info, resolveAccelerationOnLoad(info));
          reset(info);
          setStatus(installment.status);

          if (info.roasThresholds?.length > 0) {
            setRoasThresholds(mapRoasThresholdsFromApi(info.roasThresholds));
          }
          if (info.roasVector?.length > 0) {
            setRoasVector(mapRoasVectorFromApi(info.roasVector));
          }

          // Use dealType from installment response - no need to fetch deal
          setDealType(installment.info.dealType);

          setAmortizationData({
            amortization: installment.info.amortization,
            amountSold: installment.amountSold,
          });
          setTotalFeeMetrics({
            totalFeeAmount: installment.totalFeeAmount,
            totalFeePct: installment.totalFeePct,
          });

          const purchasePriceFileName = installment.info.contractFile;
          const dealContractFileName = installment.info.dealContractFile;
          const dealId = installment.info.dealId;
          if (purchasePriceFileName) {
            setPurchasePriceFileLoading(true);
          }
          if (dealContractFileName) {
            setDealContractFileLoading(true);
          }

          void loadContractDocuments({
            abortCtrl,
            purchasePriceFileName,
            dealContractFileName,
            dealId,
          }).catch(() => {});
        } else {
          const [options, _deals] = await Promise.all([p1, getDeals()]);
          if (abortCtrl.signal.aborted) return;
          setUnderwritingOptions(options);
          setDeals(_deals);
          setValue('owner', INSTALLMENT_OWNER_TYPE.TWELVE);
        }

      } finally {
        setLoading(false);
      }
    };

    init();
    return () => { abortCtrl.abort(); };
  }, []);

  useEffect(() => {
    if (installmentId) {
      getInstallmentRepayments();
    }
  }, [revenueRecognition]);

  useEffect(() => {
    setValue('amortization', amortizationData.amortization);
  }, [amortizationData]);

  useEffect(() => {
    setValue('dealType', dealType);
  }, [dealType]);

  useEffect(() => {
    if (
      formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP ||
      formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP_DAILY
    ) {
      setValue('isTrueUp', true);
      if (formValues.feeCollectionAtLastPayment == null) {
        setValue('feeCollectionAtLastPayment', false);
      }
    }
  }, [formValues.feeSettings?.feeType, formValues.feeCollectionAtLastPayment, setValue]);

  const isPageDisabled = status === INSTALLMENT_STATUS.FULLY_PAID;

  const purchasePrice = formValues.purchasePrices
    ? getTotalPurchasePrice(formValues.purchasePrices)
    : Decimal(0);

  const getUnderwritingOptions = async () => {
    try {
      const list = await getUnderwritingListApi({ customerId });
      const options = list
        .filter((u) => !!u.approvedAt)
        .map((u) => ({
          label: `${moment(u.approvedAt).format('MM-DD-YYYY')}`,
          value: u.id,
        }));

      return options;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load underwriting options',
        status: 'error',
      });
      throw error;
    }
  };

  const getDeals = async () => {
    try {
      const _deals = await getDealsApi({ customerId });
      return _deals;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to get deals',
        status: 'error',
      });
      throw error;
    }
  };

  const getInstallment = async () => {
    try {
      const installment = await getInstallmentApi({
        customerId,
        installmentId,
      });
      return installment;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to get installment data',
        status: 'error',
      });
      throw error;
    }
  };

  const getInstallmentRepayments = async () => {
    try {
      setInstallmentRepaymentsLoading(true);
      const _installmentRepayments = await getInstallmentRepaymentsApi({
        customerId,
        installmentId,
        revenueRecognition,
      });
      setInstallmentRepayments(_installmentRepayments);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to get installment repayments',
        status: 'error',
      });
      throw error;
    } finally {
      setInstallmentRepaymentsLoading(false);
    }
  };

  const saveInstallment = async (data) => {

    data.contractFile = resolvePersistedFileName(purchasePriceFile, data.contractFile);
    data.dealContractFile = resolvePersistedFileName(dealContractFile, data.dealContractFile);
    const apiFunc = installmentId
      ? () => updateInstallmentApi({ customerId, installmentId, data })
      : () => createInstallmentApi({ customerId, data });

    // Allow longer for many repayments (e.g. 30) — update + insert revision/amortizations can be slow
    const TIMEOUT_MS = 180000;
    let saveTimeoutId;
    let refreshTimeoutId;
    const timeoutPromise = (label) =>
      new Promise((_, reject) => {
        const id = setTimeout(
          () => reject(new Error(label === 'refresh' ? 'Refresh timed out' : 'Request timed out')),
          TIMEOUT_MS
        );
        if (label === 'refresh') refreshTimeoutId = id;
        else saveTimeoutId = id;
      });

    try {
      setLoadingOverlay(true);
      setSaveStepMessage('Saving installment…');
      const currentInstallmentId = await Promise.race([apiFunc(), timeoutPromise('save')]);
      if (saveTimeoutId) clearTimeout(saveTimeoutId);

      if (purchasePriceFile) {
        await createContractDocumentApi({
          customerId: customerId,
          dealOrInstallmentId: currentInstallmentId.toString(),
          file: purchasePriceFile,
        });
      }

      setSaveStepMessage('Refreshing revenue…');
      await Promise.race([
        getAmortizationApi({
          customerId,
          installmentId: currentInstallmentId,
          refreshData: true,
        }),
        timeoutPromise('refresh'),
      ]);
      if (refreshTimeoutId) clearTimeout(refreshTimeoutId);

      toast({
        title: 'Success',
        description: 'Installment updated successfully',
        status: 'success',
      });
      navigate(`/operations/${customerId}/installment`);
    } catch (error) {
      if (saveTimeoutId) clearTimeout(saveTimeoutId);
      if (refreshTimeoutId) clearTimeout(refreshTimeoutId);
      const isSaveTimeout = error?.message === 'Request timed out';
      const isRefreshTimeout = error?.message === 'Refresh timed out';
      const description = isSaveTimeout
        ? 'Saving timed out (many repayments can be slow). Check Network tab: PUT/POST to installment.'
        : isRefreshTimeout
          ? 'Revenue refresh timed out (ROAS/normalizer can be slow). Check Network tab: GET amortization?refreshData=true.'
          : error.userMessage || 'Failed to save installment';
      toast({
        title: 'Error',
        description,
        status: 'error',
      });
      throw error;
    } finally {
      setLoadingOverlay(false);
      setSaveStepMessage(null);
    }
  };

  const calcInstallmentAmortization = async () => {
    const {
      repaymentFrequency,
      repaymentDays,
      daysBeforeEndOfMonth,
      numberOfRepayments,
      feeSettings,
      firstRepaymentDate,
      cohortStartDate,
      cohortEndDate,
      pctFromRevenueShare,
      revenueTotalType,
      isTrueUp,
      transferDate,
    } = formValues;

    try {
      if (!validateDates(firstRepaymentDate, transferDate, true)) {
        return
      }
      setLoadingOverlay(true);
      const amortizationResponse = await calcInstallmentAmortizationApi({
        customerId,
        dealType,
        purchasePrice,
        repaymentFrequency,
        repaymentDays,
        daysBeforeEndOfMonth,
        numberOfRepayments,
        feeSettings,
        firstRepaymentDate,
        cohortStartDate,
        cohortEndDate,
        pctFromRevenueShare,
        revenueTotalType,
        isTrueUp,
        transferDate,
      });

      setAmortizationData(amortizationResponse);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to calculate installment amortization',
        status: 'error',
      });
      throw error;
    } finally {
      setLoadingOverlay(false);
    }
  };

  const onSubmit = (data) => {
    if (isIrrCapFeeType(data.feeSettings?.feeType)) {
      data.isTrueUp = true;
      if (data.feeCollectionAtLastPayment == null) {
        data.feeCollectionAtLastPayment = false;
      }
    }
    const accelerationValidation = validateAcceleration(data);
    if (!accelerationValidation.isValid) {
      toast({
        title: 'Validation Error',
        description: accelerationValidation.message,
        status: 'error',
      });
      return;
    }
    if (!data.accelerationEnabled) {
      data.accelerationPct = null;
      data.accelerationRevenueTotalType = null;
      data.roasThresholds = [];
    } else if (dealType === DEAL_TYPE.ROAS) {
      data.roasThresholds = mapRoasThresholdsToApi(roasThresholds);
    }
    data.roasVector = mapRoasVectorToApi(roasVector);
    if (!data.marketingViewId) {
      data.marketingViewId = null;
      data.marketingViewForRepayment = false;
    }
    if (data.roasCovenantMultiplier === '' || data.roasCovenantMultiplier == null) {
      data.roasCovenantMultiplier = null;
    } else {
      data.roasCovenantMultiplier = Number(data.roasCovenantMultiplier);
    }
    if (!data.roasCovenantRevenueTotalType) {
      data.roasCovenantRevenueTotalType = null;
    }
    delete data.accelerationEnabled;
    data.contractFile = resolvePersistedFileName(purchasePriceFile, data.contractFile);
    saveInstallment(data);
  };

  // const onSetAsFundedClick = async () => {
  //   try {
  //     setLoadingOverlay(true);
  //     await setInstallmentAsFundedApi({ customerId, installmentId });
  //     navigate(`/operations/${customerId}/installment`);
  //   } catch (error) {
  //     toast({
  //       title: 'Error',
  //       description: 'Failed to set installment as funded',
  //       status: 'error',
  //     });
  //   } finally {
  //     setLoadingOverlay(false);
  //   }
  // };

  const onDealChange = async (dealId) => {

    if (dealId)  {
      const deal = deals.find((d) => d.id === dealId);

      // console.log("onDealChange", dealId, deal);

      if (deal) {
        const newFormValues = cloneDeep(formValues);
        newFormValues.repaymentFrequency = deal.repaymentFrequency;
        newFormValues.repaymentDays = deal.repaymentDays;
        newFormValues.daysBeforeEndOfMonth = deal.daysBeforeEndOfMonth;
        newFormValues.numberOfRepayments = deal.numberOfRepayments;
        newFormValues.feeSettings = deal.feeSettings;
        newFormValues.repaymentTerms = deal.repaymentTerms;
        newFormValues.expectedIrrPct = deal.expectedIrrPct
        newFormValues.expectedDuration = deal.expectedDuration
        newFormValues.fundingPercentage = deal.fundingPercentage
        newFormValues.marketingViewId = deal.marketingViewId || ''
        newFormValues.marketingViewForRepayment = deal.marketingViewForRepayment || false
        newFormValues.maxFundingAmount = deal.maxFundingAmount
        newFormValues.maxExposure = deal.maxExposure
        newFormValues.maxMomGrowth = deal.maxMomGrowth
        newFormValues.maxMomFundingAmount = deal.maxMomFundingAmount
        newFormValues.maxCac = deal.maxCac
        newFormValues.momDeltaPct = deal.momDeltaPct
        newFormValues.momDeltaUntilRoasPct = deal.momDeltaUntilRoasPct
        newFormValues.roasCovenantRevenueTotalType = deal.roasCovenantRevenueTotalType
        newFormValues.roasCovenantMultiplier = deal.roasCovenantMultiplier
        newFormValues.pastCohortsToCheck = deal.pastCohortsToCheck
        newFormValues.minRunrate = deal.minRunrate
        newFormValues.minCashBalance = deal.minCashBalance
        newFormValues.dealType = deal.dealType

        if (deal.dealType === DEAL_TYPE.ROAS) {
          newFormValues.pctFromRevenueShare = deal.pctFromRevenueShare;
          newFormValues.revenueTotalType = deal.revenueTotalType;
          newFormValues.isTrueUp = deal.isTrueUp;
          newFormValues.accelerationPct = deal.accelerationPct;
          Object.assign(newFormValues, resolveAccelerationOnLoad(deal));
          newFormValues.feeCollectionAtLastPayment = deal.feeCollectionAtLastPayment ?? false;
          if (deal.roasThresholds?.length > 0) {
            setRoasThresholds(mapRoasThresholdsFromApi(deal.roasThresholds));
          } else {
            setRoasThresholds([{ id: 1, month: '', minRoasPct: '' }]);
          }
          if (deal.roasVector?.length > 0) {
            setRoasVector(mapRoasVectorFromApi(deal.roasVector));
          } else {
            setRoasVector([emptyRoasVectorRow()]);
          }
        } else if (deal.dealType === DEAL_TYPE.FIXED) {
          delete newFormValues.pctFromRevenueShare;
          delete newFormValues.revenueTotalType;
          delete newFormValues.isTrueUp;
          delete newFormValues.accelerationEnabled;
          delete newFormValues.accelerationPct;
          delete newFormValues.accelerationRevenueTotalType;
        }

        if(deal.contractFile) {
          setDealContractFileLoading(true);
          try {
            const file = await getContractDocumentApi({customerId, dealOrInstallmentId: dealId, fileName: deal.contractFile});
            if (
              shouldApplyFetchedContractFile({
                aborted: false,
                userAlreadySelected: dealContractUserSelectedRef.current,
              })
            ) {
              setDealContractFile(file)
              setDealContractFileContent(file);
            }
          } finally {
            setDealContractFileLoading(false);
          }
        } else {
          setDealContractFile(null)
          setDealContractFileContent(null);
          setDealContractFileLoading(false);
        }

        reset(newFormValues);
        setDealType(deal.dealType);
      }
    }
  };

  const onFieldChange = async (name, value) => {
    if (name === 'dealId') {
      await onDealChange(value);
      setValue(name, value);
      return
    }
    if (name === 'repaymentFrequency') {
      onRepaymentFrequencyFieldChange({ setValue, name, value });
      setValue(name, value);
      return
    }
    if (name === 'accelerationEnabled') {
      if (value === true) {
        if (formValues.accelerationPct == null) {
          setValue('accelerationPct', 100);
        }
        if (!formValues.accelerationRevenueTotalType) {
          setValue('accelerationRevenueTotalType', formValues.revenueTotalType);
        }
      } else {
        setValue('accelerationPct', null);
        setValue('accelerationRevenueTotalType', null);
        setRoasThresholds([{ id: 1, month: '', minRoasPct: '' }]);
      }
      setValue(name, value);
      return
    }
    if (name === 'installmentType') {
      const values = cloneDeep(formValues.purchasePrices);
      if (values) {
        const purchasePrices = []
        values.forEach((value) => {
          purchasePrices.push({
            amount: value.amount,
            bankAccountId: null,
          })
        })
        setValue('purchasePrices', purchasePrices)
      }
      setValue(name, value);
      return
    }
    if (name === 'cohortStartDate') {
      setValue('cohortStartDate', cohortMonthStart(value));
      setValue('cohortEndDate', cohortMonthEnd(value));
      return;
    }
    if (name === 'feeSettings.feeType' && (value === FEE_TYPE.IRR_CAP || value === FEE_TYPE.IRR_CAP_DAILY)) {
      setValue('isTrueUp', true);
      if (formValues.feeCollectionAtLastPayment == null) {
        setValue('feeCollectionAtLastPayment', false);
      }
      if (!formValues.feeSettings?.irrCapPeriod) {
        setValue('feeSettings.irrCapPeriod', IRR_CAP_PERIOD.ANNUAL);
      }
    }
    setValue(name, value);
  };

  function validateDates(firstRepaymentDate, transferDate, withToast) {
    const mFirstRepaymentDate = moment(firstRepaymentDate);
    const mTransferDate = moment(transferDate);
    if (!mFirstRepaymentDate.isAfter(mTransferDate)) {
      if (withToast) {
        toast({
          title: 'Error',
          description: 'First repayment date must be after transfer date',
          status: 'error',
        });
      }
      return false
    }
    return true;
  }

  const onAmortizationChange = (newAmortization) => {
    const _amortizationData = cloneDeep(amortizationData);
    newAmortization.sort((a, b) => moment(a.date).diff(moment(b.date)));
    _amortizationData.amortization = newAmortization;
    setAmortizationData(_amortizationData);
  };

  const calcAmortizationRepaymentsSum = (repayments) => {
    return Decimal.sum(
      ...repayments.map((repayment) => {
        const raw =
          repayment.actualPaidAmount ||
          repayment.amountToPay ||
          repayment.minAmount ||
          0;
        try {
          return new Decimal(raw);
        } catch {
          return new Decimal(0);
        }
      })
    );
  };

  const isAmortizationSettingsValid = () => {
    let fieldNames = [
      'repaymentFrequency',
      'repaymentDays',
      'numberOfRepayments',
      'firstRepaymentDate',
      'purchasePrices',
      'feeSettings',
    ];

    if (dealType === DEAL_TYPE.ROAS) {
      fieldNames = [
        ...fieldNames,
        'cohortStartDate',
        'cohortEndDate',
        'pctFromRevenueShare',
        'revenueTotalType',
      ];
    }

    let values = {};
    fieldNames.forEach((field) => {
      values[field] = formValues[field];
    });

    let isValid = hasObjectAllValues(values);

    if (!isValid) {
      // console.log(1)
      return false;
    }

    isValid = values.purchasePrices.every(
      (purchasePrice) =>
        !isNaN(purchasePrice.amount) &&
        purchasePrice.amount &&
          (purchasePrice.bankAccountId || formValues.installmentType === INSTALLMENT_TYPE.PREVIEW)
    );

    if (!isValid) {
      // console.log(2)
      return false;
    }

    //validate cohort dates
    if (dealType === DEAL_TYPE.ROAS) {
      const mStart = moment(values.cohortStartDate);
      const mEnd = moment(values.cohortEndDate);
      isValid =
        mStart.date() === 1 &&
        mEnd.date() === mEnd.endOf('month').date() &&
        mStart.month() === mEnd.month() &&
        mStart.year() === mEnd.year();

      if (!isValid) {
        // console.log(3,
        //   mStart.date() === 1,
        //   mEnd.date() === mEnd.endOf('month').date(),
        //   mStart.month() === mEnd.month(),
        //   mStart.year() === mEnd.year())
        return false;
      }
    }

    //validate fee settings
    if (values.feeSettings) {
      const feeType = values.feeSettings.feeType;
      isValid =
        (feeType === FEE_TYPE.FIXED && values.feeSettings.feePct) ||
        ((feeType === FEE_TYPE.IRR_CAP || feeType === FEE_TYPE.IRR_CAP_DAILY) && values.feeSettings.irrCapPct) ||
        feeType === FEE_TYPE.PROGRESSIVE;

      if (!isValid) {
        // console.log(4)
        return false;
      }
    }

    return true;
  };

  const onSelectFiles = async (files) => {
    // console.log('onSelectFiles files', files);
    let file = null
    if (files.length > 0) {
      file = files[0];
    }
    purchasePriceUserSelectedRef.current = true;
    setPurchasePriceFileLoading(false);
    setPurchasePriceFile(file);
    getFileContent(file, setPurchasePriceFileContent);
  };

  const fields = useMemo(() => {
    if (!underwritingOptions || !bankAccountOptions) {
      return [];
    }

    const dealOptions = deals.map((deal) => ({
      label: deal.name,
      value: deal.id,
    }));

    return createFields({
      underwritingOptions,
      bankAccountOptions,
      feeTypeOptions,
      repaymentDaysOptions,
      daysBeforeEndOfMonthOptions,
      dealOptions,
      formValues,
      isNewInstallment: !installmentId,
      onFieldChange,
      purchasePrice,
      dealType,
      isPageDisabled,
      purchasePriceFile,
      purchasePriceFileContent,
      purchasePriceFileLoading,
      onSelectFiles,
      marketingViewOptions,
    });
  });

  const applyMissingReasons = useMemo(
    () =>
      getInstallmentApplyMissingReasons({
        formValues,
        fields,
        purchasePriceFile,
        amortizationData,
        dealType,
        isPageDisabled,
      }),
    [
      formValues,
      fields,
      purchasePriceFile,
      amortizationData,
      dealType,
      isPageDisabled,
    ]
  );
  const isFormFilled = () => applyMissingReasons.length === 0;

  if (loading || isTwelveBankAccountsLoading) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  return (
    <OverlaySpinnerWrapper show={loadingOverlay} isFixed={true} message={saveStepMessage}>
      <Box minW={1087} maxW={'max'} fontSize={14} mt={-10}>
        <BackLink
          text={'Back to installments table'}
          path={`/operations/${customerId}/installment`}
        />
        <Card mt={'100px'}>
          <Flex align="baseline" gap={10} mb={10}>
            <Box>
              <Heading
                variant={'sectionHeader'}
                borderColor={'twelve.green.300'}
                mb={1}
              >
                Installment Setup
              </Heading>
              <Text fontSize="sm" color="gray.600">
                <Text as="span" color="red.500">*</Text> required fields
              </Text>
            </Box>
            {status && <InstallmentStatusTag status={status} />}
          </Flex>

          <form onSubmit={handleSubmit(onSubmit)}>
            <FormControl>
              <VStack spacing={12} align={'left'}>
                <InstallmentSettings
                  fields={fields.filter((field) =>
                    installmentSettingsFields.includes(field.name) && field.hidden !== true
                  )}
                  onFieldChange={onFieldChange}
                  control={control}
                  isPageDisabled={isPageDisabled}
                  dealContractFile={dealContractFile}
                  dealContractFileContent={dealContractFileContent}
                  dealContractFileLoading={dealContractFileLoading}
                  purchasePriceFile={purchasePriceFile}
                  setPurchasePriceFile={setPurchasePriceFile}
                  purchasePriceFileContent={purchasePriceFileContent}
                  setPurchasePriceFileContent={setPurchasePriceFileContent}
                />
                <RepaymentsScheduleSettings
                  fields={fields.filter((field) =>
                    (dealType === DEAL_TYPE.ROAS
                      ? repaymentScheduleRoasFields
                      : repaymentScheduleFields
                    ).includes(field.name)
                  )}
                  onFieldChange={onFieldChange}
                  control={control}
                  isPageDisabled={isPageDisabled}
                  onAmortizationChange={onAmortizationChange}
                  isAmortizationBuildDisabled={
                    isPageDisabled || !isAmortizationSettingsValid()
                  }
                  onBuildAmortization={calcInstallmentAmortization}
                  amortization={amortizationData.amortization}
                  amountSold={amortizationData.amountSold}
                  dealType={dealType}
                  feeType={formValues.feeSettings?.feeType}
                  calcAmortizationRepaymentsSum={calcAmortizationRepaymentsSum}
                  isTrueUp={formValues.isTrueUp}
                  accelerationEnabled={formValues.accelerationEnabled}
                  accelerationPct={formValues.accelerationPct}
                  roasThresholds={roasThresholds}
                  setRoasThresholds={setRoasThresholds}
                  roasVector={roasVector}
                  setRoasVector={setRoasVector}
                />
              </VStack>
              <Flex justify={'end'}>
                <TotalFeeSummary
                  totalFeeAmount={totalFeeMetrics?.totalFeeAmount}
                  totalFeePct={totalFeeMetrics?.totalFeePct}
                />
              </Flex>
              <Flex justify={'end'} mt={20} gap={4}>
                {/*{installmentType === INSTALLMENT_TYPE.PREVIEW && (*/}
                {/*  <Button*/}
                {/*    variant={'outline'}*/}
                {/*    w={200}*/}
                {/*    isDisabled={isPageDisabled || !isFormFilled()}*/}
                {/*    onClick={onSetAsFundedClick}*/}
                {/*  >*/}
                {/*    Set As Funded*/}
                {/*  </Button>*/}
                {/*)}*/}

                <Tooltip
                  label={
                    applyMissingReasons.length > 0 ? (
                      <Box>
                        <Text mb={1}>Missing or invalid:</Text>
                        {applyMissingReasons.map((reason) => (
                          <Text key={reason}>• {reason}</Text>
                        ))}
                      </Box>
                    ) : (
                      'Ready to apply'
                    )
                  }
                  hasArrow
                  placement="top"
                  openDelay={200}
                >
                  <Box>
                    <Button
                      type={'submit'}
                      variant={'brand'}
                      w={200}
                      isDisabled={isPageDisabled || !isFormFilled() || hasDuplicateRoasVectorMonths(roasVector)}
                    >
                      Apply
                    </Button>
                  </Box>
                </Tooltip>
              </Flex>
            </FormControl>
          </form>
          {installmentRepayments?.length > 0 && (
            <>
              <Divider my={10} />
              <Flex justify={'space-between'} align={'baseline'}>
                <Heading
                  variant={'sectionHeader'}
                  borderColor={'twelve.green.300'}
                  mt={2}
                >
                  Installment Repayments
                </Heading>
                <Box>
                  <RevenueRecognitionSelect
                    value={revenueRecognition}
                    onChange={setRevenueRecognition}
                  />
                </Box>
              </Flex>
              <Box mt={10} mx={-5} position={'relative'}>
                <OverlaySpinnerWrapper show={installmentRepaymentsLoading}>
                  <InstallmentRepaymentsTable
                    repayments={installmentRepayments}
                    bankAccountOptions={bankAccountOptions}
                  />
                </OverlaySpinnerWrapper>
              </Box>
            </>
          )}
        </Card>
      </Box>
    </OverlaySpinnerWrapper>
  );
};

Installment.propTypes = {};

export default Installment;
